import {
  formatBlockCommand,
  type TextFormatConfig,
  textFormatConfigs,
} from '@blocksuite/affine-inline-preset';
import {
  type TextAlignConfig,
  textAlignConfigs,
  type TextConversionConfig,
  textConversionConfigs,
} from '@blocksuite/affine-rich-text';
import {
  getSelectedModelsCommand,
  getTextSelectionCommand,
} from '@blocksuite/affine-shared/commands';
import { isInsideBlockByFlavour } from '@blocksuite/affine-shared/utils';
import {
  type SlashMenuActionItem,
  type SlashMenuConfig,
  SlashMenuConfigExtension,
  type SlashMenuItem,
  type SlashMenuTooltip,
} from '@blocksuite/affine-widget-slash-menu';
import { HeadingsIcon } from '@blocksuite/icons/lit';
import { BlockSelection } from '@blocksuite/std';

import { updateBlockAlign, updateBlockType } from '../commands';
import { tooltips } from './tooltips';

const conversionTooltips: Record<string, SlashMenuTooltip> = {
  'affine:paragraph:text': tooltips.Text,
  'affine:paragraph:h1': tooltips['Heading 1'],
  'affine:paragraph:h2': tooltips['Heading 2'],
  'affine:paragraph:h3': tooltips['Heading 3'],
  'affine:paragraph:h4': tooltips['Heading 4'],
  'affine:paragraph:h5': tooltips['Heading 5'],
  'affine:paragraph:h6': tooltips['Heading 6'],
  'affine:paragraph:quote': tooltips.Quote,
  'affine:code:': tooltips['Code Block'],
  'affine:divider:divider': tooltips.Divider,
  'affine:list:bulleted': tooltips['Bulleted List'],
  'affine:list:numbered': tooltips['Numbered List'],
  'affine:list:todo': tooltips['To-do List'],
};

const textFormatTooltips: Record<string, SlashMenuTooltip> = {
  bold: tooltips.Bold,
  italic: tooltips.Italic,
  underline: tooltips.Underline,
  strike: tooltips.Strikethrough,
};

let basicIndex = 0;
const noteSlashMenuConfig: SlashMenuConfig = {
  items: [
    ...textConversionConfigs
      .filter(i => i.type && ['h1', 'h2', 'h3', 'text'].includes(i.type))
      .map(config => createConversionItem(config, `0_基础@${basicIndex++}`)),
    {
      name: '其他标题',
      searchAlias: ['other headings'],
      icon: HeadingsIcon(),
      group: `0_基础@${basicIndex++}`,
      subMenu: textConversionConfigs
        .filter(i => i.type && ['h4', 'h5', 'h6'].includes(i.type))
        .map(config => createConversionItem(config)),
    },
    ...textConversionConfigs
      .filter(i => i.flavour === 'affine:code')
      .map(config => createConversionItem(config, `0_基础@${basicIndex++}`)),

    ...textConversionConfigs
      .filter(i => i.type && ['divider', 'quote'].includes(i.type))
      .map(
        config =>
          ({
            ...createConversionItem(config, `0_基础@${basicIndex++}`),
            when: ({ model }) =>
              model.store.schema.flavourSchemaMap.has(config.flavour) &&
              !isInsideBlockByFlavour(
                model.store,
                model,
                'affine:edgeless-text'
              ),
          }) satisfies SlashMenuActionItem
      ),

    ...textConversionConfigs
      .filter(i => i.flavour === 'affine:list')
      .map((config, index) =>
        createConversionItem(config, `1_列表@${index++}`)
      ),

    ...textAlignConfigs.map((config, index) =>
      createAlignItem(config, `2_排列@${index++}`)
    ),

    ...textFormatConfigs
      .filter(i => !['code', 'link'].includes(i.id))
      .map((config, index) =>
        createTextFormatItem(config, `2_样式@${index++}`)
      ),
  ],
};

function createConversionItem(
  config: TextConversionConfig,
  group?: SlashMenuItem['group']
): SlashMenuActionItem {
  const { name, description, icon, flavour, type, searchAlias = [] } = config;
  return {
    name,
    group,
    description,
    icon,
    searchAlias,
    tooltip: conversionTooltips[`${flavour}:${type ?? ''}`],
    when: ({ model }) => model.store.schema.flavourSchemaMap.has(flavour),
    action: ({ std }) => {
      std.command.exec(updateBlockType, {
        flavour,
        props: { type },
      });
    },
  };
}

function createAlignItem(
  config: TextAlignConfig,
  group?: SlashMenuItem['group']
): SlashMenuActionItem {
  const { textAlign, name, icon } = config;
  return {
    name,
    group,
    icon,
    action: ({ std }) => {
      std.command
        .chain()
        .pipe(getTextSelectionCommand)
        .pipe(getSelectedModelsCommand, { types: ['text'] })
        .pipe(updateBlockAlign, { textAlign })
        .run();
    },
  };
}

function createTextFormatItem(
  config: TextFormatConfig,
  group?: SlashMenuItem['group']
): SlashMenuActionItem {
  const { name, icon, id, action } = config;
  return {
    name,
    icon,
    group,
    tooltip: textFormatTooltips[id],
    action: ({ std, model }) => {
      const { host } = std;

      if (model.text?.length !== 0) {
        std.command.exec(formatBlockCommand, {
          blockSelections: [
            std.selection.create(BlockSelection, {
              blockId: model.id,
            }),
          ],
          styles: { [id]: true },
        });
      } else {
        // like format bar when the line is empty
        action(host);
      }
    },
  };
}

export const NoteSlashMenuConfigExtension = SlashMenuConfigExtension(
  'affine:note',
  noteSlashMenuConfig
);
